// Compact "Fuel Giveaway" card for /app. Shows the user's live entry total
// with a per-action breakdown, an "Enter Giveaway" CTA (opens the join modal),
// and the campaign meta headline. Auto-hides when the backend campaign is
// inactive.
//
// UX:
// - Sits inside the top-info area, not on the map.
// - Mobile-first (compact card, one column, big touch targets).
// - Never obstructs Drop / Claim / My-car controls.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Trophy, Car, MapPin, Users, Smartphone, X, Loader2, Check, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { fetchCampaignEntries, fetchPublicCampaign, joinCampaign } from "../lib/campaign";
import { shareReferral, getOwnReferralCode, fetchMyReferral } from "../lib/referral";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

const ENTRY_ICONS = {
  save_car: Car,
  drop: MapPin,
  claim: MapPin,
  referral_qualified: Users,
  install: Smartphone,
};
const ENTRY_LABELS = {
  save_car: "Save My Car",
  drop: "Valid drops",
  claim: "Valid claims",
  referral_qualified: "Qualified friends",
  install: "Install bonus",
};

// Neutral, spoiler-free earning explainer shown inside the card.
// `key` maps to the breakdown action_type so we can show a simple status
// derived from existing data only.
const EARN_ROWS = [
  { key: "save_car", icon: Car, text: "Save My Car", entries: "+1 / day" },
  { key: "install", icon: Smartphone, text: "Install / Join SpotDrop", entries: "+1 once" },
  { key: "drop", icon: MapPin, text: "Drop a valid parking spot", entries: "+2" },
  { key: "claim", icon: MapPin, text: "Claim a spot", entries: "+2" },
  { key: "referral_qualified", icon: Users, text: "Invite a friend", entries: "+3", action: "invite" },
];

/** Status text from EXISTING data only (breakdown counts + save_car_today). */
function rowStatus(key, breakdown, saveCarToday) {
  const count = breakdown?.[key]?.count || 0;
  if (key === "save_car") return saveCarToday ? "Completed today" : "Available today";
  if (key === "install") return count > 0 ? "Completed" : "Available";
  return "Available";
}

// Same strip-leading-"N ×" fallback as CampaignTeaser so stale prize strings
// stored in production don't double-print the winners multiplier.
function normalizePrize(prize) {
  if (!prize) return "";
  return String(prize).replace(/^\s*\d+\s*[×x]\s*/i, "").trim();
}

/** Loads campaign meta + this device's entries. Returns null while loading. */
function useCampaign() {
  const [meta, setMeta] = React.useState(null);
  const [entries, setEntries] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [hasReferralCode, setHasReferralCode] = React.useState(() => !!getOwnReferralCode());

  const refresh = React.useCallback(async () => {
    const [m, e] = await Promise.all([fetchPublicCampaign(), fetchCampaignEntries()]);
    setMeta(m);
    setEntries(e);
    setLoading(false);
    // Ping /referrals/mine — if this device already owns a code we can show
    // the "You're almost in!" prompt without waiting on the user to tap Invite.
    // Idempotent on the backend (returns the existing code) — safe to call.
    fetchMyReferral().then((r) => setHasReferralCode(!!(r && r.code))).catch(() => {});
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return { meta, entries, loading, refresh, hasReferralCode };
}

export default function CampaignCard() {
  const { meta, entries, loading, refresh, hasReferralCode } = useCampaign();
  const [joinOpen, setJoinOpen] = React.useState(false);
  // Auto-expand when the user hasn't joined yet — they need to see how to earn
  // entries before the CTA makes sense. Collapsed by default once they've entered.
  const total = entries?.total_entries ?? 0;
  const participant = entries?.participant || null;
  const isJoined = !!participant;
  const [expanded, setExpanded] = React.useState(() => {
    try {
      const saved = sessionStorage.getItem("spotdrop_campaign_expanded");
      if (saved !== null) return saved === "1";
    } catch {
      /* ignore */
    }
    // Compact by default on phones so the card doesn't cover the map.
    return typeof window === "undefined" ? true : window.innerWidth >= 640;
  });
  const toggleExpanded = () =>
    setExpanded((v) => {
      const next = !v;
      try {
        sessionStorage.setItem("spotdrop_campaign_expanded", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  const saveCarToday = !!entries?.save_car_today;

  if (loading) return null;
  // The card is entirely hidden while the giveaway is INACTIVE — this keeps
  // prize wording out of the DOM as required by the campaign spec.
  if (!meta || !meta.active) return null;

  const nonZero = Object.entries(entries?.breakdown || {}).filter(([, v]) => (v?.entries || 0) > 0);
  const hasEntries = nonZero.length > 0;

  return (
    <>
      <div
        className={`pointer-events-auto w-full sm:w-[calc(100vw-1.5rem)] max-w-sm rounded-2xl bg-[#0a0a0c]/70 sm:bg-[#0a0a0c]/[0.92] border border-[#ccff00]/25 backdrop-blur-md sm:backdrop-blur shadow-md sm:shadow-lg sm:px-4 sm:py-3 ${
          expanded ? "px-3 py-2.5 bg-[#0a0a0c]/[0.92]" : "px-2.5 py-1.5"
        }`}
        data-testid="campaign-card"
      >
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="shrink-0 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 flex items-center justify-center">
            <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#ccff00]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#ccff00] font-semibold flex items-center gap-1.5 truncate">
              <Trophy className="w-3 h-3 shrink-0" />
              <span className="truncate">{meta.name || "Fuel Giveaway"}</span>
            </div>
            <div
              className={`text-[11px] text-zinc-400 mt-0.5 ${expanded ? "" : "hidden sm:block"}`}
              data-testid="campaign-subhead"
            >
              {meta.total_winners > 1
                ? `${meta.total_winners} winners · €50 each`
                : normalizePrize(meta.prize)}
            </div>
          </div>
          <button
            type="button"
            onClick={toggleExpanded}
            className="w-8 h-8 sm:w-9 sm:h-9 -mr-1.5 sm:-mr-2 -mt-0.5 sm:-mt-1 shrink-0 rounded-full flex items-center justify-center text-zinc-400 hover:text-white"
            aria-label={expanded ? "Collapse" : "Expand"}
            data-testid="campaign-expand"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        <div className={`mt-3 flex-col gap-2 ${expanded ? "flex" : "hidden sm:flex"}`}>
          <div className="text-[13px] leading-tight text-zinc-300" data-testid="campaign-entries-sentence">
            You currently have{" "}
            <span className="font-heading text-xl font-bold text-[#ccff00]" data-testid="campaign-total-entries">
              {total}
            </span>{" "}
            {total === 1 ? "entry" : "entries"}
          </div>
          {isJoined ? (            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#ccff00] font-semibold flex items-center gap-1">
                <Check className="w-3 h-3" />
                Entered
              </div>
              <div
                className="text-[11px] text-zinc-400 truncate max-w-[180px]"
                data-testid="campaign-participant-contact"
                title={participant.email || participant.instagram || ""}
              >
                {participant.email || (participant.instagram ? `@${participant.instagram}` : "")}
              </div>
            </div>
          ) : (
            <Button
              type="button"
              onClick={() => setJoinOpen(true)}
              className="w-full h-10 px-4 rounded-full bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold text-sm"
              data-testid="campaign-enter-btn"
              aria-label={`Enter ${meta.prize_pool || ""} Fuel Giveaway`.trim()}
            >
              Enter {meta.prize_pool ? `${meta.prize_pool} ` : ""}Giveaway
            </Button>
          )}
        </div>

        {/* "You're almost in!" nudge for returning users who own a referral
             code but never joined the campaign. Kept in the expanded state so the
             collapsed card stays short on phones. */}
        {!isJoined && hasReferralCode && expanded && (
          <div
            className="mt-2.5 text-[11px] text-[#ccff00] leading-snug"
            data-testid="campaign-almost-in"
          >
            You&apos;re almost in! Add your email or Instagram to enter the {meta.prize_pool || "€100"} Fuel Giveaway.
          </div>
        )}

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t border-white/5 space-y-3" data-testid="campaign-breakdown">
                {hasEntries && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                      Your entries
                    </div>
                    {nonZero.map(([k, v]) => {
                      const Icon = ENTRY_ICONS[k] || Gift;
                      return (
                        <div
                          key={k}
                          className="flex items-center justify-between text-xs"
                          data-testid={`campaign-breakdown-${k}`}
                        >
                          <span className="flex items-center gap-2 text-zinc-300">
                            <Icon className="w-3.5 h-3.5 text-[#ccff00]" />
                            {ENTRY_LABELS[k] || k}
                            <span className="text-zinc-600 text-[10px]">×{v.count}</span>
                          </span>
                          <span className="font-mono text-white">+{v.entries}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-1.5" data-testid="campaign-earn-explainer">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                    Ways to earn more entries
                  </div>
                  {EARN_ROWS.map((row) => {
                    const status = rowStatus(row.key, entries?.breakdown, saveCarToday);
                    const isInvite = row.action === "invite";
                    const Wrapper = isInvite ? "button" : "div";
                    return (
                      <Wrapper
                        key={row.key}
                        {...(isInvite
                          ? {
                              type: "button",
                              onClick: () =>
                                shareReferral((kind, msg) => {
                                  if (kind === "success") toast.success(msg);
                                  else if (kind === "error") toast.error(msg);
                                }),
                            }
                          : {})}
                        className={`w-full flex items-center justify-between gap-2 text-xs min-h-[36px] text-left ${
                          isInvite ? "hover:text-white" : ""
                        }`}
                        data-testid={isInvite ? "campaign-invite-friends" : `campaign-earn-${row.key}`}
                      >
                        <span className="flex items-center gap-2 text-zinc-300 min-w-0">
                          <row.icon className="w-3.5 h-3.5 shrink-0 text-[#ccff00]" />
                          <span className="truncate">{row.text}</span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] text-zinc-500" data-testid={`campaign-status-${row.key}`}>
                            {status}
                          </span>
                          <span className="text-[#ccff00] font-mono">{row.entries}</span>
                        </span>
                      </Wrapper>
                    );
                  })}
                  <p className="text-[10px] text-zinc-600 leading-relaxed pt-1">
                    The more you help the parking community, the more chances you earn.
                  </p>
                </div>

                {meta.end_date && (
                  <div className="pt-2 border-t border-white/5 text-[10px] text-zinc-600">
                    Ends {new Date(meta.end_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                    {" · "}
                    <a
                      href={meta.terms_url || "/giveaway-terms"}
                      target={meta.terms_url && !meta.terms_url.startsWith("/") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="underline hover:text-zinc-300"
                      data-testid="campaign-terms-link"
                    >
                      Terms
                    </a>
                  </div>
                )}
                {!meta.end_date && (
                  <div className="pt-2 border-t border-white/5 text-[10px] text-zinc-600">
                    <a
                      href={meta.terms_url || "/giveaway-terms"}
                      target={meta.terms_url && !meta.terms_url.startsWith("/") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="underline hover:text-zinc-300"
                      data-testid="campaign-terms-link"
                    >
                      Read the giveaway terms
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <CampaignJoinModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onJoined={() => {
          setJoinOpen(false);
          refresh();
        }}
      />
    </>
  );
}

/** Compact modal — email OR instagram, no other PII, no password. */
export function CampaignJoinModal({ open, onClose, onJoined }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [email, setEmail] = React.useState("");
  const [instagram, setInstagram] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    const ig = instagram.trim().replace(/^@/, "");
    if (!em && !ig) {
      toast.error("Please share your email or Instagram handle.");
      return;
    }
    setLoading(true);
    try {
      await joinCampaign({ email: em || null, instagram: ig || null });
      toast.success("You're in! Good luck.");
      onJoined?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not enter the giveaway.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm"
            {...dismissProps}
            data-testid="campaign-join-backdrop"
          />
          {/* Flex-centering wrapper — avoids the framer-motion transform vs
              Tailwind '-translate-x-1/2' conflict that off-centred the modal
              on mobile. */}
          <div className="fixed inset-0 z-[1201] flex items-end md:items-center justify-center pointer-events-none px-3 pb-4 md:pb-0">
            <motion.form
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.25 }}
              onSubmit={submit}
              className="pointer-events-auto w-full max-w-md p-6 rounded-3xl bg-[#141416] border border-white/10 shadow-2xl text-left"
              data-testid="campaign-join-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Enter the giveaway"
            >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  Fuel Giveaway
                </div>
                <div className="font-heading text-xl font-semibold mt-1">
                  Enter with just an email or Instagram
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  No account required. We only use this to contact the winner.
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid="campaign-join-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
              Email
            </label>
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 h-11 bg-[#0a0a0c] border-white/10 text-white"
              data-testid="campaign-join-email"
            />

            <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-zinc-600 font-semibold">
              <span className="h-px flex-1 bg-white/5" /> OR <span className="h-px flex-1 bg-white/5" />
            </div>

            <label className="mt-3 block text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
              Instagram handle
            </label>
            <Input
              placeholder="@yourhandle"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              className="mt-1.5 h-11 bg-[#0a0a0c] border-white/10 text-white"
              data-testid="campaign-join-instagram"
            />

            <Button
              type="submit"
              disabled={loading}
              className="w-full mt-5 h-11 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold rounded-xl"
              data-testid="campaign-join-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enter Giveaway
            </Button>

            <p className="mt-3 text-[11px] text-zinc-600 leading-relaxed">
              We save your entry to this device. No password, no name — just a
              way to reach you if you win.
            </p>
            </motion.form>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
